<template>
  <div ref="componentRoot" class="mt-5">
    <div class="mb-4">
      <h3>Rechnungsadresse</h3>
    </div>

    <!-- Main Order Fields -->
    <v-text-field
      v-model="formData.mainOrder.companyName"
      label="Firmenname"
      variant="outlined"
      density="comfortable"
      :error="!!errors.companyName"
      :error-messages="errors.companyName"
      class="mb-1"
    />
    <v-text-field
      v-model="formData.mainOrder.name"
      label="Vor- und Nachname"
      variant="outlined"
      density="comfortable"
      :error="!!errors.name"
      :error-messages="errors.name"
      class="mb-1"
    />
    <v-text-field
      v-model="formData.mainOrder.address"
      label="Strasse und Nummer"
      variant="outlined"
      density="comfortable"
      :error="!!errors.address"
      :error-messages="errors.address"
      class="mb-1"
    />

    <v-row>
      <v-col cols="6">
        <v-text-field
          v-model="formData.mainOrder.postcode"
          label="Postleitzahl"
          variant="outlined"
          density="comfortable"
          :error="!!errors.postcode"
          :error-messages="errors.postcode"
          @blur="validate"
          @input="fetchAddressByPostcode('mainOrder')"
          class="mb-1"
        />
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model="formData.mainOrder.place"
          label="Ort"
          variant="outlined"
          density="comfortable"
          :loading="loadingMainOrderPlace"
          :append-inner-icon="loadingMainOrderPlace ? '' : undefined"
          :error="!!errors.place"
          :error-messages="errors.place"
          class="mb-1"
        >
          <template #append-inner>
            <v-progress-circular
              v-if="loadingMainOrderPlace"
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <v-text-field
      v-model="formData.mainOrder.phoneNumber"
      label="Telefonnummer"
      variant="outlined"
      density="comfortable"
      :error="!!errors.phoneNumber"
      :error-messages="errors.phoneNumber"
      class="mb-1"
    />
    <v-text-field
      v-model="formData.mainOrder.mail"
      label="E-Mail"
      variant="outlined"
      density="comfortable"
      :error="!!errors.mail"
      :error-messages="errors.mail"
      class="mb-1"
    />
    <v-textarea
      v-model="formData.mainOrder.comments"
      label="Bemerkungen"
      variant="outlined"
      density="comfortable"
    />

    <v-checkbox
      v-model="formData.mainOrder.useDifferentDelivery"
      label="Abweichende Lieferadresse"
      color="primary"
    />

    <!-- Delivery Address Fields -->
    <div v-if="formData.mainOrder.useDifferentDelivery">
      <div class="mb-2">
        <h3>Lieferadresse</h3>
      </div>

      <v-text-field
        v-model="formData.mainOrder.deliveryAddress.companyName"
        label="Firmenname"
        variant="outlined"
        density="comfortable"
        :error="!!errors.deliveryCompanyName"
        :error-messages="errors.deliveryCompanyName"
        class="mb-1"
      />
      <v-text-field
        v-model="formData.mainOrder.deliveryAddress.name"
        label="Name"
        variant="outlined"
        density="comfortable"
        :error="!!errors.deliveryName"
        :error-messages="errors.deliveryName"
        class="mb-1"
      />
      <v-text-field
        v-model="formData.mainOrder.deliveryAddress.address"
        label="Strasse und Nummer"
        variant="outlined"
        density="comfortable"
        :error="!!errors.deliveryAddress"
        :error-messages="errors.deliveryAddress"
        class="mb-1"
      />

      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model="formData.mainOrder.deliveryAddress.postcode"
            label="Postleitzahl"
            variant="outlined"
            density="comfortable"
            :error="!!errors.deliveryPostcode"
            :error-messages="errors.deliveryPostcode"
            @blur="validate"
            @input="fetchAddressByPostcode('delivery')"
            class="mb-1"
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="formData.mainOrder.deliveryAddress.place"
            label="Ort"
            variant="outlined"
            density="comfortable"
            :loading="loadingDeliveryPlace"
            :append-inner-icon="loadingDeliveryPlace ? '' : undefined"
            :error="!!errors.deliveryPlace"
            :error-messages="errors.deliveryPlace"
            class="mb-1"
          >
            <template #append-inner>
              <v-progress-circular
                v-if="loadingDeliveryPlace"
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </template>
          </v-text-field>
        </v-col>
      </v-row>

      <v-text-field
        v-model="formData.mainOrder.deliveryAddress.phoneNumber"
        label="Telefonnummer"
        variant="outlined"
        density="comfortable"
        :error="!!errors.deliveryPhoneNumber"
        :error-messages="errors.deliveryPhoneNumber"
        class="mb-1"
      />
      <v-text-field
        v-model="formData.mainOrder.deliveryAddress.mail"
        label="E-Mail"
        variant="outlined"
        density="comfortable"
        :error="!!errors.deliveryMail"
        :error-messages="errors.deliveryMail"
        class="mb-1"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  watch,
  onMounted,
  nextTick,
  defineExpose,
} from 'vue'
import axios from 'axios'

const props = defineProps({
  mainOrder: Object,
  deliveryAddress: Object,
})
const emit = defineEmits(['update:mainOrder', 'valid', 'heightChange'])

const componentRoot = ref(null)
let lastHeight = 0

const sendHeightUpdate = () => {
  if (!componentRoot.value) return
  const height = componentRoot.value.scrollHeight
  if (height !== lastHeight) {
    lastHeight = height
    emit('heightChange', height)
  }
}
let debounceTimeout
const debouncedHeightUpdate = (force = false) => {
  clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    if (force) sendHeightUpdate()
  }, 100)
}
defineExpose({ sendHeightUpdate })

const formData = ref({
  mainOrder: {
    ...props.mainOrder,
    address: props.mainOrder.address || '',
    postcode: props.mainOrder.postcode || '',
    place: props.mainOrder.place || '',
    deliveryAddress: props.deliveryAddress || {
      companyName: '',
      name: '',
      address: '',
      postcode: '',
      place: '',
      phoneNumber: '',
      mail: '',
    },
    useDifferentDelivery: props.mainOrder.useDifferentDelivery || false,
  },
})

const loadingMainOrderPlace = ref(false)
const loadingDeliveryPlace = ref(false)
const errors = ref({})

function validate() {
  const e = {}
  const main = formData.value.mainOrder
  const delivery = main.deliveryAddress
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phoneRegex = /^\+?[0-9\s\-()]{6,20}$/

  if (!main.name) e.name = 'Name ist erforderlich'
  if (!main.address) e.address = 'Adresse ist erforderlich'
  if (!main.postcode) e.postcode = 'Postleitzahl ist erforderlich'
  if (!main.place) e.place = 'Ort ist erforderlich'
  if (!main.phoneNumber) e.phoneNumber = 'Telefonnummer ist erforderlich'
  else if (!phoneRegex.test(main.phoneNumber))
    e.phoneNumber = 'Ungültige Telefonnummer'
  if (!main.mail) e.mail = 'E-Mail ist erforderlich'
  else if (!emailRegex.test(main.mail)) e.mail = 'Ungültige E-Mail-Adresse'

  if (main.useDifferentDelivery) {
    if (!delivery.name) e.deliveryName = 'Empfängername ist erforderlich'
    if (!delivery.address) e.deliveryAddress = 'Adresse ist erforderlich'
    if (!delivery.postcode) e.deliveryPostcode = 'Postleitzahl ist erforderlich'
    if (!delivery.place) e.deliveryPlace = 'Ort ist erforderlich'
    if (!delivery.phoneNumber)
      e.deliveryPhoneNumber = 'Telefonnummer ist erforderlich'
    else if (!phoneRegex.test(delivery.phoneNumber))
      e.deliveryPhoneNumber = 'Ungültige Telefonnummer'
    if (!delivery.mail) e.deliveryMail = 'E-Mail ist erforderlich'
    else if (!emailRegex.test(delivery.mail))
      e.deliveryMail = 'Ungültige E-Mail-Adresse'
  }

  errors.value = e
  emit('valid', Object.keys(e).length === 0)
}

watch(
  formData,
  () => {
    validate()
    emit('update:mainOrder', formData.value.mainOrder)
  },
  { deep: true },
)

watch(
  () => formData.value.mainOrder.useDifferentDelivery,
  async (newValue) => {
    if (!newValue) {
      formData.value.mainOrder.deliveryAddress = {
        companyName: '',
        name: '',
        address: '',
        postcode: '',
        place: '',
        phoneNumber: '',
        mail: '',
      }
      Object.keys(errors.value)
        .filter((k) => k.startsWith('delivery'))
        .forEach((k) => delete errors.value[k])
    }
    validate()
    await nextTick()
    sendHeightUpdate()
    debouncedHeightUpdate(true)
  },
)

async function fetchAddressByPostcode(type) {
  const target =
    type === 'mainOrder'
      ? formData.value.mainOrder
      : formData.value.mainOrder.deliveryAddress
  const loadingRef =
    type === 'mainOrder' ? loadingMainOrderPlace : loadingDeliveryPlace
  if (!target.postcode || target.postcode.length < 4) return
  loadingRef.value = true
  try {
    const response = await axios.get(
      'https://nominatim.openstreetmap.org/search',
      {
        params: {
          postalcode: target.postcode,
          country: 'Switzerland',
          format: 'json',
          'accept-language': 'de',
          addressdetails: 1,
          limit: 1,
        },
      },
    )
    if (response.data.length > 0) {
      const address = response.data[0].address
      target.place = address.city || address.town || address.village || ''
      await nextTick()
      debouncedHeightUpdate(true)
    }
  } catch (err) {
    console.error('Fehler beim Ermitteln der Gemeinde per PLZ:', err)
  } finally {
    loadingRef.value = false
  }
}

onMounted(() => {
  validate()
  setTimeout(() => debouncedHeightUpdate(true), 300)
})
</script>
